import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useMedia } from "@/features/media/hooks";
import type { MediaItem } from "@/features/media/types";
import { MediaGrid } from "./MediaGrid";
import { MediaUploader } from "./MediaUploader";

interface MediaPickerModalProps {
  isOpen: boolean;
  multiple?: boolean;
  onClose: () => void;
  onSelect: (items: MediaItem[]) => void;
}

export function MediaPickerModal({ isOpen, multiple, onClose, onSelect }: MediaPickerModalProps) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<MediaItem[]>([]);
  const media = useMedia({ page: 1, pageSize: 48, search });

  if (!isOpen) return null;

  function toggle(item: MediaItem) {
    if (!multiple) {
      setSelected([item]);
      return;
    }
    setSelected((current) => current.some((entry) => entry.id === item.id) ? current.filter((entry) => entry.id !== item.id) : [...current, item]);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/75 p-4 backdrop-blur-sm">
      <div className="mx-auto flex max-h-[calc(100vh-2rem)] max-w-6xl flex-col overflow-hidden rounded-md border border-cyan-300/20 bg-surface shadow-panel">
        <div className="flex items-start justify-between gap-4 border-b border-cyan-300/15 p-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-teal-200">Media Library</p>
            <h2 className="mt-2 font-display text-2xl font-bold uppercase text-white">Choose Image</h2>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div className="space-y-4 overflow-y-auto p-4">
          <MediaUploader />
          <input
            className="h-10 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search media"
          />
          <MediaGrid items={media.data?.items ?? []} isLoading={media.isLoading} selectedIds={selected.map((item) => item.id)} onSelect={toggle} />
        </div>
        <div className="flex justify-end gap-3 border-t border-cyan-300/15 p-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={selected.length === 0} onClick={() => { onSelect(selected); setSelected([]); onClose(); }}>
            Select {selected.length || ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
